import { Link } from "react-router-dom";
import { ArrowRight, Instagram } from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oivt9a3p__L2A0180.jpg";

/**
 * `/chronology` — George Maple's discography in order.
 *
 * Mirrors `/tour` exactly: full-bleed hero image with title overlay
 * bottom-left, then a vertical timeline of entries with year markers
 * down the left rail. Each row carries a title, a context note and an
 * optional listen link (Spotify/Apple/YouTube).
 *
 * When new releases drop, add a row to `TIMELINE` below — entries are
 * already chronological and rendered in declaration order.
 */
const TIMELINE = [
  {
    year: "2011",
    title: "Foreign Language",
    artist: "Flight Facilities (ft. George Maple)",
    note: "An early vocal feature with Flight Facilities.",
  },
  {
    year: "2012",
    title: "Uphill",
    artist: "George Maple",
    note: "The inaugural solo release.",
    link: { to: "/buy/uphill", label: "Listen / buy →" },
  },
  {
    year: "2012",
    title: "On & On",
    artist: "Snakehips (ft. George Maple)",
    note: "Picked up early by BBC Radio 1.",
  },
  {
    year: "2013",
    title: "Fixed",
    artist: "George Maple",
    note: "A signature solo cut.",
  },
  {
    year: "2015",
    title: "Talk Talk · Vacant Space · Vacant Space EP",
    artist: "George Maple",
    note: "The Vacant Space era — 'Talk Talk' would later be reworked by DJ Snake into 'Talk'.",
  },
  {
    year: "2015",
    title: "Gemini",
    artist: "What So Not (ft. George Maple)",
    note: "Collaboration with What So Not.",
  },
  {
    year: "2016",
    title: "Buried",
    artist: "What So Not (ft. George Maple)",
    note: "A second outing with What So Not.",
  },
  {
    year: "2017",
    title: "Hero · Nostalgia (GM remix) · Talk · Lover · Kryptonite · Lover (album) · Kryptonite (TCTS remix)",
    artist: "George Maple · DJ Snake",
    note: "The Lover album year — including the DJ Snake reworking of 'Talk Talk' as 'Talk', and the TCTS remix of Kryptonite.",
  },
  {
    year: "2020",
    title: "Myth",
    artist: "George Maple",
    note: "Mini album, accompanied by the self-written and directed Sydney Opera House live-performance film.",
    link: { to: "/buy/myth-film", label: "Watch the film →" },
  },
  {
    year: "2021",
    title: "Heartbeat",
    artist: "George Maple",
    note: "A return to single releases.",
  },
  {
    year: "2026",
    title: "Metamorph",
    artist: "George Maple",
    note: "The new piece of music — released on 12″ Vinyl and direct digital download alongside the live tour.",
    link: { to: "/store", label: "Get Metamorph →" },
    current: true,
  },
];

export default function Chronology() {
  return (
    <main className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white" data-testid="chronology-page">
      {/* Hero — image as background, title overlay bottom-left. Matches /tour. */}
      <div className="relative w-full aspect-[3/2] sm:aspect-[16/9] max-h-[70vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          data-testid="chronology-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--mm-bg,#050a1a)] via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
          <h1
            className="deco-text text-5xl sm:text-7xl uppercase leading-[0.95] mb-1"
            style={{ letterSpacing: "0.1em" }}
            data-testid="chronology-title"
          >
            Chronology
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/55 mb-3">
            By George Maple
          </p>
          <h2
            className="text-2xl sm:text-3xl mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
          >
            A discography &middot; in order
          </h2>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {/* Intro */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3">About</p>
        <div
          className="text-[15px] leading-[1.85] text-white/85 space-y-5 mb-12 sm:mb-14"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
          data-testid="chronology-intro"
        >
          <p>
            A chronological walk through George Maple&rsquo;s music &mdash; from the early
            vocal features and the inaugural solo release, through the Vacant Space and
            Lover eras, to the new music being released alongside the 2026 live tour.
          </p>
          <p
            className="italic border-l-2 pl-5"
            style={{ borderColor: "rgba(243,224,166,0.45)", color: "#f3e0a6" }}
          >
            Press play, scroll slowly &mdash; this is the journey, in order.
          </p>
        </div>

        {/* Timeline */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-5">Timeline</p>
        <div className="relative pl-7 sm:pl-10" data-testid="chronology-timeline">
          {/* vertical rule */}
          <div className="absolute left-0 top-2 bottom-2 w-px bg-white/15" aria-hidden />
          <div className="space-y-10 sm:space-y-12">
            {TIMELINE.map((row, i) => (
              <article key={i} className="relative" data-testid={`chronology-row-${i}`}>
                <span
                  className={`absolute -left-[31px] sm:-left-[43px] top-[6px] w-2.5 h-2.5 ${row.current ? "bg-[var(--mm-accent)] border border-[var(--mm-accent)]" : "border border-[var(--mm-accent)] bg-[var(--mm-bg,#050a1a)]"}`}
                  aria-hidden
                />
                <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-1.5">
                  {row.year}
                </p>
                <h3
                  className="text-xl sm:text-2xl mb-1 leading-snug"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
                >
                  {row.title}
                </h3>
                {row.artist && (
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55 mb-2">
                    {row.artist}
                  </p>
                )}
                {row.note && (
                  <p
                    className="text-[14px] sm:text-[15px] text-white/75 leading-[1.7]"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    {row.note}
                  </p>
                )}
                {row.link && (
                  <Link
                    to={row.link.to}
                    className="inline-flex items-center gap-1.5 mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-white/70 hover:text-[var(--mm-accent)] transition"
                    data-testid={`chronology-row-${i}-link`}
                  >
                    {row.link.label}
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* Primary CTA — back into the store after the journey */}
        <Link
          to="/store"
          className="btn-line w-full justify-center text-sm py-3.5 mt-14 mb-6"
          data-testid="chronology-to-store"
        >
          Browse the store <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
        </Link>

        {/* Followups */}
        <div className="flex flex-col sm:flex-row gap-3 mb-12" data-testid="chronology-followups">
          <Link
            to="/tour"
            className="flex-1 border border-white/20 hover:border-[var(--mm-accent)] px-5 py-3.5 transition text-center"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 block mb-1">Live</span>
            <span className="text-[15px] text-white/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              About Metamorph live &rarr;
            </span>
          </Link>
          <Link
            to="/manifesto"
            className="flex-1 border border-white/20 hover:border-[var(--mm-accent)] px-5 py-3.5 transition text-center"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 block mb-1">Read</span>
            <span className="text-[15px] text-white/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              The manifesto &rarr;
            </span>
          </Link>
        </div>

        {/* Follow */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3">Follow</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-12">
          <a
            href="https://www.instagram.com/georgemapleofficial/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between border border-white/20 hover:border-[var(--mm-accent)] px-4 py-3 transition"
            data-testid="chronology-instagram"
          >
            <span className="flex items-center gap-3">
              <Instagram className="w-4 h-4" strokeWidth={1.4} />
              <span className="text-[13px] text-white">@georgemapleofficial</span>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">Instagram &rarr;</span>
          </a>
          <a
            href="https://www.youtube.com/@GeorgeMapleMusic"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between border border-white/20 hover:border-[var(--mm-accent)] px-4 py-3 transition"
            data-testid="chronology-youtube"
          >
            <span className="text-[13px] text-white">YouTube channel</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">Watch &rarr;</span>
          </a>
        </div>

        <p className="text-center mt-12 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
          <Link to="/" className="hover:text-white/85">&larr; Back to Metamorph</Link>
        </p>
      </div>
    </main>
  );
}
